<template>
  <card-component
    truncate
    class="resource media"
    size="xsmall"
    :media-loaded="imgLoaded"
    :class="[{ moe: checkIfMoe, community: checkIfCommunity }]"
    @on-click="handlePrimaryCardAction"
  >
    <template #label>
      <span class="location">{{ location(resourceData?.resourceDomain) }}</span>
    </template>
    <template #header>
      <h6 class="title">{{ resourceData?.title }}</h6>
    </template>
    <template #thumbnail>
      <img
        v-if="showImage"
        :src="dataSrc"
        :alt="resourceData?.title"
        :data-uuid="resourceData?.uuid"
        :data-downloadable="resourceData?.downloadable"
        data-show-credit="false"
        data-show-group-access="false"
        class="thumbnail"
        @load="onImgLoaded"
      />
      <div v-else class="no-thumbnail" />
    </template>
    <template #metadata>
      <li>{{ fileTypeLabel }}</li>
    </template>
    <template #actions>
      <cv-button v-if="showMenu" class="icon-button" kind="ghost" @click="handleDownload">
        <svg-icon name="Download24" kind="action" />
      </cv-button>
    </template>
  </card-component>
</template>

<script setup lang="ts">
import { useStore } from "vuex";
import {
  NAMESPACE,
  ADD_FROM_LOCATION,
  RESULT_CARD_TYPE,
} from "@modules/search/service/search.constant";
import mediaObjectApi from "@apis/media.object.api";
import SearchResourceDetailContentSubpage from "@modules/search/components/SearchResourceDetailContentSubpage";
import SearchSelectionMixin from "@modules/search/mixin/search.selection.mixin";
import LessonCardMixin from "@plugins/common/mixin/lesson.card.mixin";
import ResourceMixin from "@plugins/common/mixin/resource.mixin";
import StringUtils from "@utils/string.utils";
import { ref, computed, watch, onMounted, inject } from "vue";
import { useVm } from "@utils/vm.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { performFileDownload } from "@utils/download.utils.ts";

// Mixin
defineOptions({
  mixins: [LessonCardMixin, ResourceMixin, SearchSelectionMixin],
});

// Inject
const searchContext = inject("searchContext");

// Props
interface Props {
  resourceData?: Record<string, unknown>;
  fileType?: string | null;
  size?: string;
  searchFilters?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  resourceData: null,
  fileType: null,
  size: "xsmall",
  searchFilters: () => ({}),
});

// Emits
const emit = defineEmits<{
  actionBack: [];
  selectionSaved: [data?: unknown];
}>();

// Composables
const { vm } = useVm();
const authStore = useStore();
const { passiveApiFailHandler } = useErrorHandler();
const contentSubpageInstance = useContentSubpageInstance();

// Data
const mediaObject = ref({});
const isLoading = ref(true);
const dataSrc = ref("");
const imgLoaded = ref(false);

// Computed
const isStudent = computed(() => authStore.getters[`${NAMESPACE.AUTH}/isStudent`]);

const showImage = computed(() => {
  return mediaObject.value?.fileType !== "HTML5";
});

const actionTriggeredOnCardClick = computed(() => {
  // Based on card patterns, the action that should trigger upon clicking the card
  // is the card's last available action.
  // Hence checking of which action is allowed must be done in sequence.
  if (vm.isSingleSelection) return handleChooseSelection;
  return handleViewDetails;
});

const showMenu = computed(() => {
  return props.resourceData?.downloadable;
});

const isAddFromLibraryOrStarredResources = computed(() => {
  if (!searchContext?.addFromLocation) return false;
  return (
    searchContext.addFromLocation === ADD_FROM_LOCATION.LIBRARY ||
    searchContext.addFromLocation === ADD_FROM_LOCATION.STARRED_RESOURCES
  );
});

const fileTypeLabel = computed(() => {
  return StringUtils.toTitleCase(props.fileType);
});

// Methods
const fetchMediaObjectMetadata = () => {
  isLoading.value = true;
  mediaObjectApi.getMetaData({ uuid: props.resourceData?.uuid }).then((response) => {
    //OK
    if (response) {
      mediaObject.value = response.data;
      if (!mediaObject.value.accessible) {
        //todo: render non available mediaObject
      } else {
        if (mediaObject.value.fileType === "IMAGE") {
          dataSrc.value = mediaObject.value.fileUrl;
        } else if (mediaObject.value.fileType === "VIDEO") {
          dataSrc.value = mediaObject.value.thumbnailUrl;
        }
      }
      isLoading.value = false;
    }
  }, passiveApiFailHandler); //OK
};

const handleViewDetails = () => {
  contentSubpageInstance.show(
    SearchResourceDetailContentSubpage,
    {
      baseTitle: vm.subpageTitle,
      hasParentSubpage: vm.isSelection,
      uuid: props.resourceData?.uuid,
      title: props.resourceData?.title,
      location: vm.location(props.resourceData?.resourceDomain),
      type: "MEDIA",
      hasSelectAction: !isStudent.value && isAddFromLibraryOrStarredResources.value, // Show Choose button
      addFromLocation: searchContext?.addFromLocation,
      searchFilters: props.searchFilters,
      actionBack: () => {
        emit("actionBack");
      },
    },
    { resourceSelected: handleResourceSelected },
  );
};

const handlePrimaryCardAction = () => {
  const action = actionTriggeredOnCardClick.value;
  action();
};

const handleResourceSelected = (data) => {
  addSelection();
  emit("selectionSaved", data);
};

const handleDownload = () => {
  const uuid = mediaObject.value.uuid;
  mediaObjectApi.downloadMediaObject({ uuid: uuid }).then((response) => {
    //OK
    if (response.code === "ok") {
      performFileDownload(response.data);
    } else {
      //todo: error message
    }
  }, passiveApiFailHandler); //OK
};

const handleChooseSelection = () => {
  addSelection();
  emit("selectionSaved");
};

const onImgLoaded = () => {
  imgLoaded.value = true;
};

const addSelection = () => {
  vm.isSelectedCard = true;
  vm.store.setSelectionList({
    uuid: props.resourceData?.uuid,
    type:
      searchContext.resourceType === RESULT_CARD_TYPE.MEDIA
        ? props.fileType
        : props.resourceData?.questionType,
    title: props.resourceData?.title,
    resourceDomain: props.resourceData?.resourceDomain,
    description: props.resourceData?.description,
    dataSrc: dataSrc.value,
  });
};

// Watch
watch(
  () => props.resourceData,
  () => {
    fetchMediaObjectMetadata();
  },
);

// Lifecycle hooks
onMounted(() => {
  fetchMediaObjectMetadata();
  dataSrc.value = (props.resourceData && props.resourceData?.src) || "";
});
</script>
